import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { db } from "../../firebase";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";

const servers = {
  iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
};

const ChatRoom = () => {
  const { roomId } = useParams();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState("");

  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnection = useRef(null);
  const candidateQueue = useRef([]);
  const messagesEndRef = useRef(null);

  // 🔐 VERIFY ROOM
  const verifyRoom = async () => {
    if (!username || !password) {
      setError("Enter username and password");
      return;
    }

    const roomRef = doc(db, "rooms", roomId);
    const roomSnap = await getDoc(roomRef);

    if (!roomSnap.exists()) {
      setError("Room does not exist");
      return;
    }

    if (roomSnap.data().password !== password) {
      setError("Incorrect password");
      return;
    }

    setIsAuthenticated(true);
    setError("");
  };

  // 📩 LOAD CHAT
  useEffect(() => {
    if (!isAuthenticated) return;

    const q = query(
      collection(db, "rooms", roomId, "messages"),
      orderBy("createdAt")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMessages(
        snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
      );
    });

    return () => unsubscribe();
  }, [roomId, isAuthenticated]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    await addDoc(collection(db, "rooms", roomId, "messages"), {
      text: input,
      username,
      createdAt: serverTimestamp(),
    });

    setInput("");
  };

  // 🎥 START CAMERA
  const startCamera = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });

    setLocalStream(stream);
    localVideoRef.current.srcObject = stream;
  };

  // 📞 CREATE CALL
  const createCall = async () => {
    if (!localStream) {
      alert("Start camera first");
      return;
    }

    peerConnection.current = new RTCPeerConnection(servers);

    localStream.getTracks().forEach((track) => {
      peerConnection.current.addTrack(track, localStream);
    });

    const callDoc = doc(db, "calls", roomId);
    const offerCandidates = collection(callDoc, "offerCandidates");
    const answerCandidates = collection(callDoc, "answerCandidates");

    // Collect ICE
    peerConnection.current.onicecandidate = (event) => {
      if (event.candidate) {
        addDoc(offerCandidates, event.candidate.toJSON());
      }
    };

    // Create Offer
    const offerDescription = await peerConnection.current.createOffer();
    await peerConnection.current.setLocalDescription(offerDescription);

    await setDoc(callDoc, { offer: offerDescription });

    // Listen for Answer
    onSnapshot(callDoc, async (snapshot) => {
      const data = snapshot.data();

      if (!peerConnection.current.currentRemoteDescription && data?.answer) {
        await peerConnection.current.setRemoteDescription(
          new RTCSessionDescription(data.answer)
        );

        // 🔥 Flush queued ICE
        for (const candidate of candidateQueue.current) {
          await peerConnection.current.addIceCandidate(candidate);
        }
        candidateQueue.current = [];
      }
    });

    // Listen for Answer ICE
    onSnapshot(answerCandidates, (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        if (change.type === "added") {
          const candidate = new RTCIceCandidate(change.doc.data());

          if (peerConnection.current.remoteDescription) {
            await peerConnection.current.addIceCandidate(candidate);
          } else {
            candidateQueue.current.push(candidate);
          }
        }
      });
    });

    // Remote stream
    peerConnection.current.ontrack = (event) => {
      remoteVideoRef.current.srcObject = event.streams[0];
    };
  };

  // 📲 ANSWER CALL
  const answerCall = async () => {
    if (!localStream) {
      alert("Start camera first");
      return;
    }

    peerConnection.current = new RTCPeerConnection(servers);

    localStream.getTracks().forEach((track) => {
      peerConnection.current.addTrack(track, localStream);
    });

    const callDoc = doc(db, "calls", roomId);
    const offerCandidates = collection(callDoc, "offerCandidates");
    const answerCandidates = collection(callDoc, "answerCandidates");

    peerConnection.current.onicecandidate = (event) => {
      if (event.candidate) {
        addDoc(answerCandidates, event.candidate.toJSON());
      }
    };

    const callData = (await getDoc(callDoc)).data();

    await peerConnection.current.setRemoteDescription(
      new RTCSessionDescription(callData.offer)
    );

    const answerDescription = await peerConnection.current.createAnswer();
    await peerConnection.current.setLocalDescription(answerDescription);

    await setDoc(callDoc, { answer: answerDescription }, { merge: true });

    // 🔥 Flush queued ICE
    for (const candidate of candidateQueue.current) {
      await peerConnection.current.addIceCandidate(candidate);
    }
    candidateQueue.current = [];

    // Listen for Offer ICE
    onSnapshot(offerCandidates, (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        if (change.type === "added") {
          const candidate = new RTCIceCandidate(change.doc.data());

          if (peerConnection.current.remoteDescription) {
            await peerConnection.current.addIceCandidate(candidate);
          } else {
            candidateQueue.current.push(candidate);
          }
        }
      });
    });

    peerConnection.current.ontrack = (event) => {
      remoteVideoRef.current.srcObject = event.streams[0];
    };
  };

  // ❌ END CALL
  const endCall = () => {
    peerConnection.current?.close();
    setRemoteStream(null);
  };

  // 🔐 LOGIN SCREEN
  if (!isAuthenticated) {
    return (
      <div style={styles.container}>
        <h2>Join Room: {roomId}</h2>
        <input
          placeholder="Enter Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          style={styles.input}
        />
        <input
          placeholder="Enter Room Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={styles.input}
        />
        <button onClick={verifyRoom} style={styles.sendBtn}>
          Join Room
        </button>
        {error && <p style={{ color: "red" }}>{error}</p>}
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2>Room: {roomId}</h2>

      {/* 🎥 VIDEO SECTION */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
        <video
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
          style={{ width: "45%", border: "1px solid #ccc" }}
        />
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          style={{ width: "45%", border: "1px solid #ccc" }}
        />
      </div>

      <div style={{ marginBottom: "10px" }}>
        <button onClick={startCamera}>Start Camera</button>
        <button onClick={createCall}>Call</button>
        <button onClick={answerCall}>Answer</button>
        <button onClick={endCall}>End</button>
      </div>

      {/* 💬 CHAT */}
      <div style={styles.chatBox}>
        {messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              ...styles.message,
              alignSelf: msg.username === username ? "flex-end" : "flex-start",
              backgroundColor:
                msg.username === username ? "#4CAF50" : "#e5e5ea",
              color: msg.username === username ? "white" : "black",
            }}
          >
            <strong>{msg.username}</strong>
            <div>{msg.text}</div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div style={styles.inputContainer}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type message..."
          style={styles.input}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
        />
        <button onClick={sendMessage} style={styles.sendBtn}>
          Send
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: "700px",
    margin: "40px auto",
    padding: "20px",
    border: "1px solid #ddd",
    borderRadius: "10px",
  },
  chatBox: {
    height: "300px",
    overflowY: "auto",
    border: "1px solid #ddd",
    padding: "10px",
    display: "flex",
    flexDirection: "column",
    marginBottom: "10px",
  },
  message: {
    padding: "8px",
    borderRadius: "8px",
    marginBottom: "6px",
    maxWidth: "70%",
  },
  inputContainer: { display: "flex" },
  input: { flex: 1, padding: "8px" },
  sendBtn: {
    marginLeft: "10px",
    padding: "8px 12px",
    background: "#4CAF50",
    color: "white",
    border: "none",
    cursor: "pointer",
  },
};

export default ChatRoom;
